import type { ChannelLegendData } from '../ChannelLegendItem'

/**
 * One flag's worth of data: every displayed channel whose event falls on the
 * same chart day, plus the position + event date the caller needs to render the
 * flag and its popover. The event itself is caller-defined; this layer only
 * groups by date.
 */
export interface EventDateGroup {
  id: string
  /**
   * UTC-midnight epoch the flag is pinned to. Matches the chart's x-domain
   * convention (calendar Y/M/D pinned to UTC midnight) so the flag lands on the
   * same x-scale as the axis ticks.
   */
  positionTimestamp: number
  /** Event date of the first channel that day — drives the popover label. */
  eventDate: Date
  /** Channels whose event falls on this day, in the order they were passed in. */
  channels: ChannelLegendData[]
}

// Mirror the chart's `toUtcMidnight`: take the date's calendar Y/M/D and pin it
// to UTC midnight so a flag aligns with the chart's x-domain. (Tests run in UTC,
// where calendar components equal UTC components.)
function toChartDayTs(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
}

/**
 * Groups the displayed channels into per-day event annotations — one group per
 * chart day, holding the channels whose event lands on it. Channels whose event
 * predates the visible range (the common case) are skipped: there's nothing on
 * the timeline to anchor a flag to. Pure; the grouping rules are the tests'
 * source of truth, and the caller turns each group into a `ChartAnnotation`.
 */
export function groupChannelsByEventDate(
  channels: ChannelLegendData[],
  eventDateById: Map<string, string | Date | null | undefined>,
  startDate: Date,
  endDate: Date,
): EventDateGroup[] {
  const rangeStart = toChartDayTs(startDate)
  const rangeEnd = toChartDayTs(endDate)

  const groups = new Map<number, EventDateGroup>()

  for (const channel of channels) {
    const rawEventDate = eventDateById.get(channel.id)
    if (rawEventDate == null) continue
    const eventDate = new Date(rawEventDate)
    if (Number.isNaN(eventDate.getTime())) continue

    const positionTimestamp = toChartDayTs(eventDate)
    if (positionTimestamp < rangeStart || positionTimestamp > rangeEnd) continue

    const existing = groups.get(positionTimestamp)
    if (existing) {
      existing.channels.push(channel)
    } else {
      groups.set(positionTimestamp, {
        id: String(positionTimestamp),
        positionTimestamp,
        eventDate,
        channels: [channel],
      })
    }
  }

  return [...groups.values()].toSorted(
    (a, b) => a.positionTimestamp - b.positionTimestamp,
  )
}

const DAY_MS = 86_400_000

export type AnnotationBucketUnit = 'day' | 'week' | 'month'

/**
 * Several event days collapsed into one flag, so flags read as a tidy row
 * instead of scattered buttons. The flag sits at the members' centroid; on hover
 * the chart draws a faint line at each member day.
 */
export interface EventDateBucket {
  id: string
  /** Flag position: centroid of the member days (UTC-midnight epoch). */
  positionTimestamp: number
  /** Each member day's UTC-midnight epoch — drives the faint per-date lines. */
  dayTimestamps: number[]
  /** The per-day groups in this bucket, in date order. */
  days: EventDateGroup[]
}

/**
 * Evenly-spaced x-axis ticks snapped to UTC-midnight day boundaries. recharts'
 * auto ticks land on arbitrary "nice" timestamps that don't fall on calendar
 * days, so a flag pinned to a real day doesn't sit under its date label. Feeding
 * the chart these ticks keeps the labels on day boundaries that match the flags.
 */
export function buildDayAlignedTicks(
  startDate: Date,
  endDate: Date,
  maxTicks = 8,
): number[] {
  const startTs = toChartDayTs(startDate)
  const endTs = toChartDayTs(endDate)
  const spanDays = Math.round((endTs - startTs) / DAY_MS)
  if (spanDays <= 0) return [startTs]
  const stepDays = Math.max(1, Math.ceil(spanDays / Math.max(1, maxTicks - 1)))
  const ticks: number[] = []
  for (let ts = startTs; ts <= endTs; ts += stepDays * DAY_MS) ticks.push(ts)
  if (ticks[ticks.length - 1] !== endTs) ticks.push(endTs)
  return ticks
}

/**
 * Bucket granularity scaled to the visible span so flags neither scatter (too
 * fine) nor over-collapse (too coarse): day for ≤ ~1 month, week for ≤ ~6
 * months, month beyond that.
 */
export function pickAnnotationBucketUnit(
  startDate: Date,
  endDate: Date,
): AnnotationBucketUnit {
  const spanDays =
    Math.round((toChartDayTs(endDate) - toChartDayTs(startDate)) / DAY_MS) + 1
  if (spanDays <= 31) return 'day'
  if (spanDays <= 183) return 'week'
  return 'month'
}

// The timestamp every day in the same bucket shares. Days are UTC-midnight epochs
// (see `toChartDayTs`), so derive the bucket from their UTC parts.
function bucketKeyFor(dayTs: number, unit: AnnotationBucketUnit): number {
  if (unit === 'day') return dayTs
  const date = new Date(dayTs)
  if (unit === 'week') {
    // Monday-anchored week (matches the parked time-series bucketing).
    const mondayOffset = (date.getUTCDay() + 6) % 7
    return dayTs - mondayOffset * DAY_MS
  }
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)
}

/**
 * Groups per-day event annotations into time buckets at the given granularity.
 * Each bucket's flag is positioned at the centroid of its member days; the
 * members are kept (date-ordered) for the popover and guide lines. Pure.
 */
export function bucketEventDateGroups(
  days: EventDateGroup[],
  unit: AnnotationBucketUnit,
): EventDateBucket[] {
  const buckets = new Map<number, EventDateGroup[]>()
  for (const day of days) {
    const key = bucketKeyFor(day.positionTimestamp, unit)
    const existing = buckets.get(key)
    if (existing) existing.push(day)
    else buckets.set(key, [day])
  }

  return [...buckets.entries()]
    .map(([key, members]) => {
      const sortedDays = members.toSorted(
        (a, b) => a.positionTimestamp - b.positionTimestamp,
      )
      const dayTimestamps = sortedDays.map((day) => day.positionTimestamp)
      const positionTimestamp = Math.round(
        dayTimestamps.reduce((sum, ts) => sum + ts, 0) / dayTimestamps.length,
      )
      return {
        id: String(key),
        positionTimestamp,
        dayTimestamps,
        days: sortedDays,
      }
    })
    .toSorted((a, b) => a.positionTimestamp - b.positionTimestamp)
}

/** A flag's date rendered for display, e.g. "Feb 6, 2026". */
export function formatEventDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Accessible label for a bucket's flag. `phrase` is a count-neutral verb phrase
 * with no trailing preposition — the label appends " on {date}" itself, so pass
 * "connected", not "connected on". It stays grammatical for both singular and
 * plural, and names the day (or range) so grouped flags on different dates don't
 * collapse to the same accessible name.
 */
export function buildAnnotationAriaLabel(
  bucket: EventDateBucket,
  phrase: string,
): string {
  const channelCount = bucket.days.reduce(
    (total, day) => total + day.channels.length,
    0,
  )
  const firstDay = bucket.days[0]
  const lastDay = bucket.days[bucket.days.length - 1]
  const bucketDateLabel =
    firstDay === lastDay
      ? formatEventDate(firstDay.eventDate)
      : `${formatEventDate(firstDay.eventDate)} to ${formatEventDate(lastDay.eventDate)}`

  return channelCount === 1
    ? `${firstDay.channels[0].name} ${phrase} on ${formatEventDate(firstDay.eventDate)}`
    : `${channelCount} channels ${phrase} on ${bucketDateLabel}`
}
